import { useMemo, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import DataTable, { Column } from "@/components/ui/DataTable";
import { useGetInventoryMarginQuery } from "@/redux/services/inventory/inventoryReport.api";
import { MarginReportRow } from "@/redux/services/inventory/inventory.type";
import { displayPrice, cn } from "@/lib/utils";
import { useRestaurantCurrency } from "@/lib/hooks";
import { Download } from "lucide-react";

interface MarginReportProps {
  restaurantId: string;
}

type SortKey = "title" | "price" | "cogs" | "margin" | "marginBps";
type SortOrder = "asc" | "desc";

const exportCsv = (rows: MarginReportRow[], currency: string) => {
  const header = ["Plat", "Menu", "Prix", "Coût", "Marge", "Marge %"];
  const lines = rows.map((r) => {
    const hasRecipe = r.cogs > 0 || r.margin !== r.price;
    return [
      r.title,
      r.menuTitle ? r.menuTitle : "",
      String(r.price / 100),
      hasRecipe ? String(r.cogs / 100) : "",
      hasRecipe ? String(r.margin / 100) : "",
      hasRecipe ? (r.marginBps / 100).toFixed(2) : "",
    ];
  });
  const csv = [header, ...lines]
    .map((row) =>
      row
        .map((cell) => {
          const v = cell ?? "";
          return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
        })
        .join(",")
    )
    .join("\n");
  const stamp = new Date().toISOString().slice(0, 10);
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `marge-${stamp}-${currency}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const MarginReport = ({ restaurantId }: MarginReportProps) => {
  const currency = useRestaurantCurrency();
  const [sortBy, setSortBy] = useState<SortKey>("marginBps");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [search, setSearch] = useState("");

  const { data, isLoading } = useGetInventoryMarginQuery({ restaurantId });

  // A row has a recipe if cogs > 0 (or its margin doesn't match its price -
  // covers free items where price=0 cogs=0 separately, but those are noise)
  const hasRecipe = (r: MarginReportRow) => r.cogs > 0;

  const rows = data?.rows ?? [];

  const sortedFiltered = useMemo(() => {
    const q = search.toLowerCase();
    const base = rows.filter(
      (r) =>
        r.title.toLowerCase().includes(q) ||
        (r.menuTitle ?? "").toLowerCase().includes(q)
    );
    const sorted = [...base].sort((a, b) => {
      const va = a[sortBy];
      const vb = b[sortBy];
      if (typeof va === "number" && typeof vb === "number") {
        return sortOrder === "asc" ? va - vb : vb - va;
      }
      return sortOrder === "asc"
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    });
    return sorted;
  }, [rows, sortBy, sortOrder, search]);

  // KPIs
  const dishesWithRecipe = useMemo(() => rows.filter(hasRecipe), [rows]);
  const avgMarginBps = useMemo(() => {
    if (dishesWithRecipe.length === 0) return null;
    const sum = dishesWithRecipe.reduce((acc, r) => acc + r.marginBps, 0);
    return sum / dishesWithRecipe.length;
  }, [dishesWithRecipe]);
  const lossCount = useMemo(
    () => rows.filter((r) => hasRecipe(r) && r.marginBps < 0).length,
    [rows]
  );
  const noRecipeCount = useMemo(
    () => rows.filter((r) => !hasRecipe(r)).length,
    [rows]
  );

  if (isLoading) {
    return <Skeleton className="w-full h-60" />;
  }

  if (!data || rows.length === 0) {
    return (
      <div className="dash__card text-center py-8">
        <p className="text-muted-foreground text-sm">
          Aucune donnée de marge (les prix et coûts doivent être renseignés)
        </p>
      </div>
    );
  }

  const columns: Column[] = [
    {
      key: "title",
      label: "Plat",
      sortable: true,
      formatter: (_v, row: MarginReportRow) => (
        <div className="flex flex-col">
          <span className="font-medium">{row.title}</span>
          {row.menuTitle && (
            <span className="text-xs text-muted-foreground">
              {row.menuTitle}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "price",
      label: "Prix",
      sortable: true,
      formatter: (_v, row: MarginReportRow) =>
        displayPrice(row.price, currency),
    },
    {
      key: "cogs",
      label: "Coût",
      sortable: true,
      formatter: (_v, row: MarginReportRow) =>
        hasRecipe(row) ? (
          displayPrice(row.cogs, currency)
        ) : (
          <span className="text-muted-foreground italic">—</span>
        ),
    },
    {
      key: "margin",
      label: "Marge",
      sortable: true,
      formatter: (_v, row: MarginReportRow) =>
        hasRecipe(row) ? (
          displayPrice(row.margin, currency)
        ) : (
          <span className="text-muted-foreground italic">—</span>
        ),
    },
    {
      key: "marginBps",
      label: "Marge %",
      sortable: true,
      formatter: (_v, row: MarginReportRow) => {
        if (!hasRecipe(row)) {
          return (
            <span className="text-muted-foreground italic text-xs">
              Recette non définie
            </span>
          );
        }
        const pct = row.marginBps / 100;
        const isNegative = row.marginBps < 0;
        const isThin = row.marginBps >= 0 && row.marginBps < 2000;
        return (
          <span
            className={cn(
              "font-medium",
              isNegative
                ? "text-red-600"
                : isThin
                ? "text-amber-600"
                : "text-green-600"
            )}
          >
            {pct.toFixed(2)}%
          </span>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      {/* KPI strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="dash__card">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">
            Marge moyenne
          </p>
          <p className="text-2xl font-semibold text-card-foreground mt-1">
            {avgMarginBps === null
              ? "—"
              : `${(avgMarginBps / 100).toFixed(1)}%`}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {dishesWithRecipe.length} plat
            {dishesWithRecipe.length !== 1 ? "s" : ""} avec recette
          </p>
        </div>
        <div className="dash__card">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">
            Plats en perte
          </p>
          <p
            className={cn(
              "text-2xl font-semibold mt-1",
              lossCount > 0 ? "text-red-600" : "text-card-foreground"
            )}
          >
            {lossCount}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Marge négative
          </p>
        </div>
        <div className="dash__card">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">
            Plats sans recette
          </p>
          <p
            className={cn(
              "text-2xl font-semibold mt-1",
              noRecipeCount > 0 ? "text-amber-600" : "text-card-foreground"
            )}
          >
            {noRecipeCount}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            À configurer
          </p>
        </div>
      </div>

      {/* Export */}
      <div className="flex justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => exportCsv(rows, currency)}
          disabled={rows.length === 0}
        >
          <Download className="h-4 w-4" />
          Exporter (CSV)
        </Button>
      </div>

      <DataTable
        data={sortedFiltered}
        columns={columns}
        totalCount={sortedFiltered.length}
        searchable={true}
        searchValue={search}
        onSearch={setSearch}
        paginated={false}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={(key, order) => {
          setSortBy(key as SortKey);
          setSortOrder(order);
        }}
        emptyMessage="Aucun résultat"
      />
    </div>
  );
};

export default MarginReport;
